<template>
  <b-modal v-model="visible" size="xl" centered hide-footer no-close-on-backdrop :no-close-on-esc="saving" body-class="p-0" @hidden="reset">
    <template #modal-title>
      <span class="wtb-title"><i class="fas fa-file-circle-plus"/>{{ $t('whatsappHub.builder.title') }}</span>
    </template>

    <div class="wtb">
      <div class="wtb-main">
        <!-- identity -->
        <div class="wtb-grid2">
          <b-form-group :label="$t('whatsappHub.builder.name')" :description="$t('whatsappHub.builder.nameHint')" class="mb-0">
            <b-form-input v-model="form.friendlyName" size="sm" placeholder="checkin_instructions"/>
          </b-form-group>
          <b-form-group :label="$t('whatsappHub.builder.language')" class="mb-0">
            <b-form-select v-model="form.language" size="sm" :options="languages"/>
          </b-form-group>
        </div>

        <!-- type -->
        <div>
          <label class="wtb-label">{{ $t('whatsappHub.builder.type') }}</label>
          <div class="wtb-types">
            <button v-for="t in types" :key="t.value" type="button" class="wtb-type" :class="{'wtb-type--on': form.type === t.value}" @click="form.type = t.value">
              <i :class="t.icon"/><b>{{ $t('whatsappHub.builder.types.' + t.value) }}</b>
              <small>{{ $t('whatsappHub.builder.typeHints.' + t.value) }}</small>
            </button>
          </div>
        </div>

        <!-- body -->
        <div v-if="needsBody">
          <label class="wtb-label">{{ $t('whatsappHub.builder.body') }}
            <span class="wtb-label__tools">
              <b-button variant="outline-secondary" size="sm" @click="insertVariable"><i class="fas fa-tag mr-25"/>{{ $t('whatsappHub.builder.insertVariable') }}</b-button>
            </span>
          </label>
          <b-form-textarea ref="bodyInput" v-model="form.body" rows="5" :placeholder="$t('whatsappHub.builder.bodyPlaceholder')"/>
          <small class="wtb-hint">{{ $t('whatsappHub.builder.bodyHint') }}</small>
        </div>

        <!-- card -->
        <div v-if="form.type === 'CARD'" class="wtb-grid2">
          <b-form-group :label="$t('whatsappHub.builder.cardTitle')" class="mb-0"><b-form-input v-model="form.title" size="sm"/></b-form-group>
          <b-form-group :label="$t('whatsappHub.builder.cardSubtitle')" class="mb-0"><b-form-input v-model="form.subtitle" size="sm"/></b-form-group>
        </div>

        <!-- media -->
        <div v-if="form.type === 'MEDIA' || form.type === 'CARD'">
          <label class="wtb-label">{{ $t('whatsappHub.builder.media') }}</label>
          <div v-for="(url, i) in form.mediaUrls" :key="'m' + i" class="wtb-row">
            <b-form-input v-model="form.mediaUrls[i]" size="sm" placeholder="https://…/image.jpg"/>
            <button type="button" class="wtb-x" @click="form.mediaUrls.splice(i, 1)"><i class="fas fa-times"/></button>
          </div>
          <b-button variant="flat-secondary" size="sm" :disabled="form.mediaUrls.length >= 1 && form.type === 'MEDIA'" @click="form.mediaUrls.push('')">
            <i class="fas fa-plus mr-25"/>{{ $t('whatsappHub.builder.mediaAdd') }}
          </b-button>
          <small class="wtb-hint d-block">{{ $t('whatsappHub.builder.mediaHint') }}</small>
        </div>

        <!-- quick replies / call to action / card buttons -->
        <div v-if="form.type === 'QUICK_REPLY' || form.type === 'CALL_TO_ACTION' || form.type === 'CARD'">
          <label class="wtb-label">{{ $t('whatsappHub.builder.actions') }} <small class="wtb-hint">{{ $t('whatsappHub.builder.actionsHint.' + form.type) }}</small></label>
          <div v-for="(a, i) in form.actions" :key="'a' + i" class="wtb-row wtb-row--action">
            <b-form-select v-if="form.type !== 'QUICK_REPLY'" v-model="a.type" size="sm" :options="actionTypes" class="wtb-action-type"/>
            <b-form-input v-model="a.title" size="sm" :placeholder="$t('whatsappHub.builder.actionTitle')" maxlength="25"/>
            <b-form-input v-if="a.type === 'URL'" v-model="a.url" size="sm" placeholder="https://…"/>
            <b-form-input v-else-if="a.type === 'PHONE_NUMBER'" v-model="a.phone" size="sm" placeholder="+43…"/>
            <b-form-input v-else v-model="a.id" size="sm" :placeholder="$t('whatsappHub.builder.actionId')"/>
            <button type="button" class="wtb-x" @click="form.actions.splice(i, 1)"><i class="fas fa-times"/></button>
          </div>
          <b-button variant="flat-secondary" size="sm" :disabled="form.actions.length >= maxActions" @click="addAction">
            <i class="fas fa-plus mr-25"/>{{ $t('whatsappHub.builder.addAction') }}
          </b-button>
        </div>

        <!-- list picker -->
        <div v-if="form.type === 'LIST_PICKER'">
          <b-form-group :label="$t('whatsappHub.builder.listButton')" class="mb-50"><b-form-input v-model="form.button" size="sm" maxlength="20" placeholder="Options"/></b-form-group>
          <label class="wtb-label">{{ $t('whatsappHub.builder.items') }}</label>
          <div v-for="(item, i) in form.items" :key="'i' + i" class="wtb-row wtb-row--action">
            <b-form-input v-model="item.item" size="sm" :placeholder="$t('whatsappHub.builder.itemTitle')" maxlength="24"/>
            <b-form-input v-model="item.description" size="sm" :placeholder="$t('whatsappHub.builder.itemDescription')" maxlength="72"/>
            <b-form-input v-model="item.id" size="sm" :placeholder="$t('whatsappHub.builder.actionId')"/>
            <button type="button" class="wtb-x" @click="form.items.splice(i, 1)"><i class="fas fa-times"/></button>
          </div>
          <b-button variant="flat-secondary" size="sm" :disabled="form.items.length >= 10" @click="form.items.push({item: '', description: '', id: ''})">
            <i class="fas fa-plus mr-25"/>{{ $t('whatsappHub.builder.addItem') }}
          </b-button>
        </div>

        <!-- location -->
        <div v-if="form.type === 'LOCATION'" class="wtb-grid3">
          <b-form-group :label="$t('whatsappHub.builder.latitude')" class="mb-0"><b-form-input v-model="form.latitude" size="sm" type="number" step="any" placeholder="48.2082"/></b-form-group>
          <b-form-group :label="$t('whatsappHub.builder.longitude')" class="mb-0"><b-form-input v-model="form.longitude" size="sm" type="number" step="any" placeholder="16.3738"/></b-form-group>
          <b-form-group :label="$t('whatsappHub.builder.label')" class="mb-0"><b-form-input v-model="form.label" size="sm"/></b-form-group>
        </div>

        <!-- authentication -->
        <div v-if="form.type === 'AUTHENTICATION'" class="wtb-grid3">
          <b-form-checkbox v-model="form.addSecurityRecommendation" switch class="wtb-toggle">{{ $t('whatsappHub.builder.authSecurity') }}</b-form-checkbox>
          <b-form-group :label="$t('whatsappHub.builder.authExpiry')" class="mb-0"><b-form-input v-model="form.codeExpirationMinutes" size="sm" type="number" min="1" max="90"/></b-form-group>
          <b-form-group :label="$t('whatsappHub.builder.copyCodeText')" class="mb-0"><b-form-input v-model="form.button" size="sm" maxlength="25" placeholder="Copy code"/></b-form-group>
        </div>

        <!-- variable samples -->
        <div v-if="variableKeys.length">
          <label class="wtb-label">{{ $t('whatsappHub.builder.samples') }} <small class="wtb-hint">{{ $t('whatsappHub.builder.samplesHint') }}</small></label>
          <div class="wtb-samples">
            <div v-for="key in variableKeys" :key="key" class="wtb-sample">
              <span class="wtb-sample__key tabular" v-text="'{{' + key + '}}'"/>
              <b-form-input v-model="form.variables[key]" size="sm" :placeholder="$t('whatsappHub.builder.sampleFor', {n: key})"/>
            </div>
          </div>
        </div>

        <!-- approval -->
        <div class="wtb-approval">
          <b-form-checkbox v-model="form.submitForApproval" switch class="wtb-toggle wtb-toggle--big">{{ $t('whatsappHub.builder.submitForApproval') }}</b-form-checkbox>
          <div v-if="form.submitForApproval" class="wtb-grid2 mt-50">
            <b-form-group :label="$t('whatsappHub.templates.waName')" :description="$t('whatsappHub.templates.waNameHint')" class="mb-0">
              <b-form-input v-model="form.approvalName" size="sm" @input="approvalNameTouched = true; form.approvalName = slug(form.approvalName)"/>
            </b-form-group>
            <b-form-group :label="$t('whatsappHub.templates.category')" class="mb-0">
              <b-form-select v-model="form.category" size="sm" :options="categories"/>
              <small class="wtb-hint">{{ $t('whatsappHub.templates.categories.' + form.category) }}</small>
            </b-form-group>
          </div>
          <small v-else class="wtb-hint d-block mt-25">{{ $t('whatsappHub.builder.noApprovalHint') }}</small>
        </div>
      </div>

      <aside class="wtb-side">
        <span class="wtb-side__head">{{ $t('whatsappHub.builder.preview') }}</span>
        <whatsapp-phone-preview :type="form.type" :body="form.body" :title="form.title" :subtitle="form.subtitle" :button="form.button" :label="form.label"
                                :media-urls="form.mediaUrls.filter(Boolean)" :actions="form.actions" :items="form.items" :variables="form.variables"
                                :security-recommendation="form.addSecurityRecommendation" :sender="status ? status.branchName : ''"
                                :empty-text="$t('whatsappHub.builder.previewEmpty')"/>
      </aside>

      <div class="wtb-foot">
        <span v-if="error" class="wtb-error mr-auto"><i class="fas fa-triangle-exclamation mr-50"/>{{ error }}</span>
        <b-button variant="flat-secondary" :disabled="saving" @click="visible = false">{{ $t('whatsappHub.composer.close') }}</b-button>
        <b-button variant="success" :disabled="!valid || saving" @click="save">
          <b-spinner v-if="saving" small class="mr-50"/><i v-else class="fas fa-check mr-50"/>
          {{ saving ? $t('whatsappHub.builder.creating') : form.submitForApproval ? $t('whatsappHub.builder.createAndSubmit') : $t('whatsappHub.builder.create') }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {mapActions} from 'vuex'
import WhatsappPhonePreview from './WhatsappPhonePreview.vue'
import {LANGUAGES, TEMPLATE_TYPES} from './whatsappUtils'

function emptyForm() {
  return {
    friendlyName: '', language: 'en', type: 'TEXT', body: '', title: '', subtitle: '', button: '', label: '',
    mediaUrls: [], actions: [], items: [], variables: {}, latitude: null, longitude: null,
    addSecurityRecommendation: true, codeExpirationMinutes: 10,
    submitForApproval: true, approvalName: '', category: 'UTILITY',
  };
}

/**
 * Builds a Twilio Content template of any WhatsApp-capable type and
 * (optionally) submits it to WhatsApp for approval in one go.
 */
export default {
  name: 'WhatsappTemplateBuilder',
  components: {WhatsappPhonePreview},
  props: {
    buid: {type: [Number, String], default: null},
    status: {type: Object, default: null},
  },
  data: () => ({
    visible: false,
    saving: false,
    error: null,
    form: emptyForm(),
    languages: LANGUAGES,
    types: TEMPLATE_TYPES,
    actionTypes: [{value: 'URL', text: 'URL'}, {value: 'PHONE_NUMBER', text: 'Phone'}, {value: 'QUICK_REPLY', text: 'Quick reply'}],
    categories: [{value: 'UTILITY', text: 'UTILITY'}, {value: 'MARKETING', text: 'MARKETING'}, {value: 'AUTHENTICATION', text: 'AUTHENTICATION'}],
  }),
  computed: {
    needsBody() {
      return !['CARD', 'LOCATION', 'AUTHENTICATION'].includes(this.form.type);
    },
    maxActions() {
      return this.form.type === 'QUICK_REPLY' ? 3 : this.form.type === 'CARD' ? 3 : 2;
    },
    variableKeys() {
      const keys = [];
      [this.form.body, this.form.title, this.form.subtitle].forEach(text => {
        const re = /\{\{\s*(\d+)\s*}}/g;
        let m;
        while (text && (m = re.exec(text)) != null) if (!keys.includes(m[1])) keys.push(m[1]);
      });
      return keys;
    },
    valid() {
      const f = this.form;
      if (!f.friendlyName.trim()) return false;
      if (this.needsBody && !f.body.trim()) return false;
      if (f.type === 'MEDIA' && f.mediaUrls.filter(Boolean).length === 0) return false;
      if (f.type === 'QUICK_REPLY' && f.actions.filter(a => a.title).length === 0) return false;
      if (f.type === 'CALL_TO_ACTION' && f.actions.filter(a => a.title && (a.url || a.phone)).length === 0) return false;
      if (f.type === 'LIST_PICKER' && f.items.filter(i => i.item).length === 0) return false;
      if (f.type === 'CARD' && !f.title.trim()) return false;
      if (f.type === 'LOCATION' && (f.latitude == null || f.longitude == null || f.latitude === '' || f.longitude === '')) return false;
      if (f.submitForApproval && !f.approvalName) return false;
      return true;
    },
  },
  watch: {
    'form.friendlyName'(name) {
      if (!this.approvalNameTouched) this.form.approvalName = this.slug(name);
    },
    'form.type'(type) {
      if (type === 'AUTHENTICATION') this.form.category = 'AUTHENTICATION';
      else if (this.form.category === 'AUTHENTICATION') this.form.category = 'UTILITY';
      if ((type === 'QUICK_REPLY' || type === 'CALL_TO_ACTION' || type === 'CARD') && this.form.actions.length === 0) this.addAction();
      if (type === 'LIST_PICKER' && this.form.items.length === 0) this.form.items.push({item: '', description: '', id: ''});
      if ((type === 'MEDIA' || type === 'CARD') && this.form.mediaUrls.length === 0) this.form.mediaUrls.push('');
    },
    variableKeys(keys) {
      keys.forEach(k => { if (this.form.variables[k] == null) this.$set(this.form.variables, k, ''); });
    },
  },
  methods: {
    ...mapActions('whatsapp', ['hubCreateTemplate']),
    open(preset) {
      this.form = Object.assign(emptyForm(), preset || {});
      this.approvalNameTouched = false;
      this.error = null;
      this.visible = true;
    },
    reset() {
      this.form = emptyForm();
      this.error = null;
    },
    slug(name) {
      return String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+/, '');
    },
    addAction() {
      const type = this.form.type === 'QUICK_REPLY' ? 'QUICK_REPLY' : 'URL';
      this.form.actions.push({type, title: '', url: '', phone: '', id: ''});
    },
    insertVariable() {
      const next = (this.variableKeys.length + 1);
      const token = '{{' + next + '}}';
      const el = this.$refs.bodyInput && this.$refs.bodyInput.$el;
      if (el && typeof el.selectionStart === 'number') {
        const start = el.selectionStart, end = el.selectionEnd;
        this.form.body = this.form.body.substring(0, start) + token + this.form.body.substring(end);
        this.$nextTick(() => { el.focus(); el.setSelectionRange(start + token.length, start + token.length); });
      } else {
        this.form.body += token;
      }
    },
    save() {
      if (!this.valid) return;
      this.saving = true;
      this.error = null;
      const f = this.form;
      const request = {
        buid: this.buid,
        friendlyName: f.friendlyName.trim(),
        language: f.language,
        type: f.type,
        body: this.needsBody ? f.body : null,
        title: f.title || null,
        subtitle: f.subtitle || null,
        button: f.button || null,
        label: f.label || null,
        mediaUrls: f.mediaUrls.filter(Boolean),
        actions: f.actions.filter(a => a.title).map(a => ({type: a.type, title: a.title, url: a.url || null, phone: a.phone || null, id: a.id || null})),
        items: f.items.filter(i => i.item).map(i => ({item: i.item, description: i.description || null, id: i.id || null})),
        variables: f.variables,
        latitude: f.latitude == null || f.latitude === '' ? null : Number(f.latitude),
        longitude: f.longitude == null || f.longitude === '' ? null : Number(f.longitude),
        addSecurityRecommendation: f.addSecurityRecommendation,
        codeExpirationMinutes: Number(f.codeExpirationMinutes) || 10,
        submitForApproval: f.submitForApproval,
        approvalName: f.approvalName,
        category: f.category,
        allowCategoryChange: true,
      };
      this.hubCreateTemplate(request).then(template => {
        this.$emit('created', template);
        this.visible = false;
      }).catch(error => this.error = error).finally(() => this.saving = false);
    },
  }
}
</script>

<style lang="scss" scoped>
.tabular { font-variant-numeric: tabular-nums; }
.wtb-title { display: inline-flex; align-items: center; gap: 8px; font-weight: 800; i { color: #25d366; } }
.wtb { display: grid; grid-template-columns: minmax(0, 1fr) 320px; grid-template-rows: 1fr auto; }
.wtb-main { padding: 16px 18px; display: flex; flex-direction: column; gap: 14px; max-height: 70vh; overflow-y: auto; }
.wtb-side { grid-row: 1; grid-column: 2; padding: 16px 18px; background: rgba(15, 23, 42, .03); border-left: 1px solid rgba(15, 23, 42, .06); display: flex; flex-direction: column; gap: 10px; }
.wtb-side__head { font-size: .68rem; font-weight: 800; text-transform: uppercase; letter-spacing: .06em; color: #94a3b8; }
.wtb-foot { grid-column: 1 / -1; display: flex; align-items: center; justify-content: flex-end; gap: 8px; padding: 12px 18px; border-top: 1px solid rgba(15, 23, 42, .06); }
.wtb-error { color: #ea5455; font-size: .78rem; font-weight: 600; }
.wtb-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.wtb-grid3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; align-items: end; }
.wtb-label { display: flex; align-items: center; gap: 8px; font-size: .68rem; font-weight: 800; text-transform: uppercase; letter-spacing: .06em; color: #94a3b8; margin-bottom: 6px; }
.wtb-label__tools { margin-left: auto; text-transform: none; letter-spacing: 0; }
.wtb-hint { font-size: .66rem; color: #94a3b8; text-transform: none; letter-spacing: 0; font-weight: 500; }
.wtb-toggle { margin: 0; font-size: .76rem; font-weight: 700; color: #64748b; &--big { font-size: .84rem; color: #1f2a44; } :deep(.custom-control-label) { cursor: pointer; } }
.wtb-types { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; }
.wtb-type {
  display: flex; flex-direction: column; align-items: flex-start; gap: 1px; text-align: left;
  border: 1px solid rgba(15, 23, 42, .1); border-radius: 10px; background: #fff; padding: 8px 10px; cursor: pointer; transition: all .15s;
  i { font-size: .9rem; color: #94a3b8; margin-bottom: 3px; }
  b { font-size: .74rem; color: #1f2a44; }
  small { font-size: .62rem; color: #94a3b8; line-height: 1.25; }
  &:hover { border-color: rgba(37, 211, 102, .5); }
  &:focus { outline: none; }
  &--on { border-color: #25d366; background: rgba(37, 211, 102, .08); i { color: #1f9d57; } }
}
.wtb-row { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.wtb-row--action { display: grid; grid-template-columns: 1fr 1fr 1fr 28px; }
.wtb-action-type { max-width: 130px; }
.wtb-x { width: 28px; height: 28px; border: none; border-radius: 8px; background: rgba(15, 23, 42, .05); color: #94a3b8; cursor: pointer; &:hover { color: #ea5455; } &:focus { outline: none; } }
.wtb-samples { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 6px; }
.wtb-sample { display: grid; grid-template-columns: 52px 1fr; align-items: center; gap: 6px; }
.wtb-sample__key { font-size: .72rem; font-weight: 800; color: #7367f0; }
.wtb-approval { border: 1px solid rgba(37, 211, 102, .35); background: rgba(37, 211, 102, .05); border-radius: 12px; padding: 10px 12px; }
@media (max-width: 992px) {
  .wtb { grid-template-columns: 1fr; }
  .wtb-side { grid-row: auto; grid-column: 1; border-left: none; border-top: 1px solid rgba(15, 23, 42, .06); }
  .wtb-types { grid-template-columns: repeat(2, 1fr); }
  .wtb-grid2, .wtb-grid3 { grid-template-columns: 1fr; }
  .wtb-row--action { grid-template-columns: 1fr 28px; }
}
</style>

<style lang="scss">
.dark-layout {
  .wtb-side { background: rgba(255, 255, 255, .03); border-color: rgba(255, 255, 255, .08); }
  .wtb-foot { border-color: rgba(255, 255, 255, .08); }
  .wtb-type { background: #283046; border-color: rgba(255, 255, 255, .12); b { color: #e9ecf2; } }
  .wtb-type--on { background: rgba(37, 211, 102, .12); border-color: #25d366; }
  .wtb-toggle--big { color: #e9ecf2; }
  .wtb-x { background: rgba(255, 255, 255, .08); }
  .wtb-approval { background: rgba(37, 211, 102, .06); }
}
</style>
